import { useEffect, useState, useRef, FC } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RenderIf } from 'components/RenderIf'

import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'

import { Spinner } from 'featureSliced/entities/Spinner'
import { Button } from 'featureSliced/entities/Button'
import { Scanner } from 'featureSliced/entities/Scanner'

import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import { openLink } from 'featureSliced/shared/utils/openLink'

import { ModalsSlice } from 'featureSliced/shared/store/reducers/ModalsReducer'

import {
  BranchPage,
  BranchTitle,
  TableInputContainer,
  TablePageContainer,
  TableInput,
  NumberDiv,
  OrJustHint,
  ButtonContainer,
  TableTop,
  TableCaptionError,
  SelectRoomTitle,
  RoomContainer,
} from './styled'
import { qrMenuGET } from 'featureSliced/shared/api/qrMenu/GET'
import { ITable } from 'featureSliced/shared/store/reducers/CommonReducer/types'
import { CommonSlice } from 'featureSliced/shared/store/reducers/CommonReducer'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import { RestaurantsCheckBox } from 'featureSliced/padges/Welcome/styledComponents'

type TableSelectProps = {
  loadData?: (tableId?: string | number) => void
  onClose: (tableId?: string | number) => void
  /**
   * Opened to CHANGE an already-chosen table rather than to ask for a missing one
   * (QRM-641: the web layout's table card). Without it the modal closes itself the
   * moment it mounts — see openedWithTableId below.
   */
  isReselect?: boolean
}

export const TableSelect: FC<TableSelectProps> = ({
  loadData,
  onClose,
  isReselect = false,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const openModal = useOpenModal()
  const [searchParams, setSearchParams] = useSearchParams()
  const { guid, registerId, dataEnc } = handleLocalStorage([
    'guid',
    'registerId',
    'dataEnc',
  ])

  // Store
  const tableData = useMySelector((state) => state.common.tableData)
  const { isUrlExtended } = useMySelector((state) => state.app.menu)

  // Actions
  const { closeModal } = ModalsSlice.actions
  const openToast = ToastsSlice.actions.openToast
  const setTableData = CommonSlice.actions.setTableData

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [isTableExist, setIsTableExist] = useState(true)
  const [selectedTableCaption, setSelectedTableCaption] = useState<string>('')
  const [selectedTableId, setSelectedTableId] = useState<number>()
  const [tablesArray, setTablesArray] = useState<ITable[]>([])
  const [step, setStep] = useState<'table' | 'room'>('table')
  // const [selectedTableId, setStep] = useState<'table' | 'room'>('table')

  const inputRef = useRef<HTMLInputElement>(null)

  // useEffects
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
  }, [registerId, dataEnc])

  // What makes this component a prompt: a table that is already known (?table=, the
  // scanner) selects itself and closes the modal on mount. A deliberate re-pick has
  // to stay open until the guest chooses, so that first pass is skipped for the
  // table we opened with.
  const openedWithTableId = useRef(tableData?.id)

  useEffect(() => {
    if (!tableData?.id) return
    if (isReselect && tableData.id === openedWithTableId.current) return
    onTableIdSelected()
  }, [tableData?.id])

  // Functions
  // Re-picking the SAME table leaves tableData.id untouched, so the effect above
  // never runs and the modal would hang open — close it here instead.
  const commitTable = (table: ITable) => {
    dispatch(setTableData(table))
    if (isReselect && table.id === openedWithTableId.current) {
      onTableIdSelected()
    }
  }
  const onTableIdSelected = async () => {
    searchParams.delete('table')
    searchParams.append('table', `${tableData.id}`)
    await setSearchParams(searchParams)
    if (((guid && registerId) || dataEnc) && tableData.id) {
      loadData?.(tableData.id)
      onClose(tableData.id)
      dispatch(closeModal('SEARCH_PARAMS_PROMPT'))
      dispatch(closeModal('TABLE_SELECTION'))
    }
  }

  const setTableDataToStore = () => {
    if (selectedTableId) {
      const selectedTableData = tablesArray?.find(
        (table) => table.id === selectedTableId
      )
      if (selectedTableData) {
        commitTable(selectedTableData)
      }
    }
  }

  const loadTableData = async () => {
    try {
      setIsLoading(true)
      setIsTableExist(true)
      if ((guid || dataEnc) && selectedTableCaption) {
        const tablesArrayReq = await qrMenuGET.tableInfo(selectedTableCaption)
        if (tablesArrayReq?.length > 1) {
          setTablesArray(tablesArrayReq)
          setStep('room')
        } else if (tablesArrayReq?.length === 1) {
          commitTable(tablesArrayReq[0])
        } else {
          openToast({
            message: t('mainPage.couldNotFindTable'),
          })
        }
      }
    } catch (e) {
      setIsTableExist(false)
    } finally {
      setIsLoading(false)
    }
  }

  const openScanner = () => {
    if (registerId || dataEnc) {
      openModal({
        id: 'BARCODE_SCANNER',
        title: 'Scan',
        components: [
          () => (
            <Scanner
              title={t('scanner.scanQRCode')}
              subtitle={t('scanner.pointCamera')}
              onScan={openLink}
            />
          ),
        ],
      })
    }
  }

  return (
    <BranchPage>
      <RenderIf condition={isLoading}>
        <Spinner type="transparent" />
      </RenderIf>
      <RenderIf condition={step === 'table'}>
        <TablePageContainer>
          <TableTop>
            <BranchTitle>{t('scanner.enterTableNumber')}</BranchTitle>
            <TableInputContainer>
              <NumberDiv>№</NumberDiv>
              <TableInput
                ref={inputRef}
                value={selectedTableCaption}
                onChange={(e) => setSelectedTableCaption(e.currentTarget.value)}
              />
              <Button
                buttonType="common"
                height="58px"
                width="58px"
                onClick={loadTableData}
                disabled={isLoading}
              >
                {t('buttons.ok')}
              </Button>
            </TableInputContainer>
            <TableCaptionError>
              {isTableExist ? '' : t('scanner.tableDoesNotExist')}
            </TableCaptionError>
          </TableTop>
          <RenderIf condition={Boolean(isUrlExtended)}>
            <ButtonContainer>
              <OrJustHint>{t('scanner.orJust')}</OrJustHint>
              <Button buttonType="stroke" width="60%" onClick={openScanner}>
                {t('scanner.scanQRCode')}
              </Button>
            </ButtonContainer>
          </RenderIf>
        </TablePageContainer>
      </RenderIf>
      <RenderIf condition={step === 'room'}>
        <SelectRoomTitle>{t('mainPage.selectYourRoom')}</SelectRoomTitle>
        {tablesArray?.map((el) => (
          <RoomContainer key={el.id} onClick={() => setSelectedTableId(el.id)}>
            <p>{el.roomCaption}</p>
            <RestaurantsCheckBox isSelected={selectedTableId === el.id} />
          </RoomContainer>
        ))}
        <Button
          buttonType="common"
          width="calc(100% + 30px)"
          onClick={setTableDataToStore}
          disabled={!selectedTableId}
        >
          {t('buttons.select')}
        </Button>
      </RenderIf>
    </BranchPage>
  )
}

export default TableSelect
